/**
 * Attachment Storage Utilities
 *
 * Provides functions for uploading chat attachments (PDFs, DOCXs) to Firebase Storage
 * and serializing them with storage URLs for cross-device/cross-session persistence.
 *
 * This solves the problem of attachments being unavailable after page refresh when
 * loaded from Firestore (which can't store the base64 dataUrl due to 1MB doc limit).
 */

import {
  storage,
  getAttachmentStoragePath,
  uploadBytes,
  getDownloadURL,
  ref,
} from './firebaseStorage';
import { getAuth } from 'firebase/auth';
import type { SerializedAttachment } from '../components/GlobalChatSystem/types';
import { isInlineEligible } from '../services/prest0nSession/client';
import { beginUploadActivity } from './attachmentUploadProgress';
import { readAttachmentImageMeta, type AttachmentImageArtifacts } from './attachmentImageMeta';
import { withDeadline } from './withDeadline';
import {
  PROXY_LANE_MAX_BYTES,
  UploadCancelledError,
  UploadSizeMismatchError,
  cancelRegisteredUpload,
  clearUploadCancelled,
  isUploadCancelled,
  markUploadCancelled,
} from './uploadShared';
import { loadLargeUploadLane, type LargeUploadLaneModule } from './largeUploadLaneDoor';

export {
  GENERIC_CONTENT_TYPE,
  PROXY_LANE_MAX_BYTES,
  UploadCancelledError,
  UploadSizeMismatchError,
  UploadStalledError,
} from './uploadShared';

// Must match the CF base URL used in src/services/prest0nSession/client.ts
const CF_BASE_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

/**
 * Uploads already in flight, started when the user PICKED the file rather
 * than when they pressed send (2026-09-11).
 *
 * Nothing uploaded at pick time before this: `ADD_ATTACHMENTS` stored raw
 * File objects and the first byte went out ~100 lines into `handleSend`. So
 * the whole transfer sat between "send" and the message reaching anyone —
 * on a shared thread the turn POST waits for the full upload by design, so a
 * 4MB screenshot on a phone held the TEXT back too. Starting at pick time
 * spends the seconds the user is typing, and changes not one byte of what is
 * uploaded (images are never re-encoded — owner ruling, 2026-09-11).
 *
 * A WeakMap because it is only ever read by File identity, never enumerated:
 * an attachment the user removes takes its entry with it when the File is
 * collected, so there is no cleanup path to forget to call.
 */
const inFlightByFile = new WeakMap<File, Promise<AttachmentWithStorage>>();

/**
 * The path segment an upload started before its message existed.
 *
 * The segment is a namespace: `USERS_PATH_RE` in attachmentContentResolver.ts
 * captures the UID only, and `authorizeSharedThreadAttachmentPath` matches the
 * whole path against the locators the shared message doc itself lists.
 *
 * But nothing in the MESSAGE names it (corrected 2026-10-07). A record that
 * reached another device before its upload landed carries no path, and the
 * server's fallback rebuilt only `attachments/{messageId}/{name}`, so it could
 * never find a draft upload: Miles's "10 minute audio" thread showed
 * "attachment unavailable" on the desktop for two files that were sitting in
 * the bucket. The server now finds such a file through the turn that carried
 * it (`taskId` in the resolve context; attachmentTurnLookup.ts).
 */
const draftPathSegment = (): string =>
  `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Begin uploading a file the user just attached. Idempotent per File, and
 * never throws — a failure here is indistinguishable from not having
 * prewarmed at all, because the send path awaits the same promise and
 * `serializeAttachmentWithStorage` already resolves (never rejects) with a
 * locator-less record when an upload fails.
 *
 * A FAILED upload is forgotten (2026-10-07). The cache used to hold whatever
 * the first attempt resolved to, so the send path's later retries and the
 * bubble's retry all got the same locator-less record back and never uploaded
 * again: the file stayed on the sender's device and nowhere else. Now an
 * upload that came back without a location leaves the cache the moment it
 * settles (this handler is attached first, so it runs before any caller's
 * await resumes), and the next ask uploads afresh. A successful upload stays
 * cached: one transfer per file.
 */
export function prewarmAttachmentUpload(file: File, uid: string | undefined): void {
  if (!file || !uid) return;               // guest surfaces: send-time upload, as before
  if (inFlightByFile.has(file)) return;
  clearUploadCancelled(file);              // attached again: it is wanted after all
  const upload = serializeAttachmentWithStorage(file, uid, draftPathSegment());
  inFlightByFile.set(file, upload);
  const forgetIfFailed = (record?: AttachmentWithStorage | null): void => {
    if (record && (record.storagePath || record.storageUrl)) return;
    if (inFlightByFile.get(file) === upload) inFlightByFile.delete(file);
  };
  upload.then(forgetIfFailed, () => forgetIfFailed(null));
}

/**
 * Result of a storage upload operation
 */
export interface StorageUploadResult {
  /** Whether the upload succeeded */
  success: boolean;
  /** Firebase Storage download URL (if successful) */
  storageUrl?: string;
  /** Storage path for reference/deletion (if successful) */
  storagePath?: string;
  /**
   * The stored object's byte count, read back from storage (2026-10-08). A
   * success always carries it when the lane can say, and it always equals the
   * picked file's size: an upload whose stored length differs is a failure,
   * never a delivered file.
   */
  storedBytes?: number;
  /** Error details (if failed) */
  error?: Error;
}

/**
 * Serialized attachment with Firebase Storage fields
 */
export interface AttachmentWithStorage extends SerializedAttachment {
  /** Firebase Storage download URL */
  storageUrl?: string;
  /** Storage path for reference/deletion */
  storagePath?: string;
}

/**
 * ── HOW LONG ONE DIRECT PUT IS GIVEN ──────────────────────────────────────
 *
 * It used to be a flat five minutes with the comment "5 min for large files".
 * That is a number about a 50 MB file wearing a comment about large ones, and
 * `aSizeCapMustBeTransferable` (2026-09-13) was written to say so BEFORE the
 * cap moved: raise the ceiling alone and a clean immediate rejection becomes a
 * five-minute wait followed by a failure, which is strictly worse for the
 * person uploading.
 *
 * So the timeout is derived from the file rather than guessed. The floor
 * bandwidth is the judgement, stated out loud: 10 Mbit/s, the slow end of a
 * real office line — NOT the 100 Mbit/s the pin uses as its optimistic
 * reference, because a timeout sized on a good connection is exactly the one
 * that fires on a bad one. At the single PUT's own ceiling (SINGLE_PUT_MAX_BYTES,
 * 1 GiB) that is ~14.3 minutes, inside the signed URL's 30-minute life with
 * room to spare; the relationship between those two is what the pin asserts.
 * The PRODUCT cap (50 GB since 2026-10-08) never travels this way: it goes by
 * the resumable lane, which is judged by progress and has no such deadline.
 *
 * The floor of five minutes keeps every small upload behaving as it does
 * today: a wedged connection on a 2 MB attachment must not be given a quarter
 * of an hour to prove it.
 */
export const UPLOAD_FLOOR_BYTES_PER_SEC = (10 * 1_000_000) / 8;
export const MIN_PUT_TIMEOUT_MS = 300_000;
export function putTimeoutMsForBytes(bytes: number): number {
  const needed = Math.ceil((Math.max(0, bytes) / UPLOAD_FLOOR_BYTES_PER_SEC) * 1000);
  return Math.max(MIN_PUT_TIMEOUT_MS, needed);
}

/**
 * ── WHICH LANE CARRIES WHICH FILE (2026-10-08, the 50 GB raise) ───────────
 *
 * The proxy lane carries a file up to PROXY_LANE_MAX_BYTES (uploadShared.ts
 * says why that number). Every larger file, and a smaller one the proxy could
 * not place, goes by the direct, resumable lane: largeUploadLane.ts, its own
 * chunk, fetched the first time a file needs it.
 *
 * SINGLE_PUT_MAX_BYTES — the signed-URL PUT carries a file in ONE request
 * with no resume, inside a 30-minute URL (aSizeCapMustBeTransferable proves
 * the fit at this size). It is a rescue lane now, never the way a large file
 * travels, and it refuses anything larger rather than starting a transfer that
 * cannot finish.
 */
export const SINGLE_PUT_MAX_BYTES = 1024 * 1024 * 1024;

/** Bounds on the waits before a transfer (CLAUDE.md: every send-path wait is bounded). */
const ID_TOKEN_DEADLINE_MS = 20_000;
/** Fetching the direct lane's chunk (a few KB) before a file can use it. */
const LANE_LOAD_DEADLINE_MS = 60_000;
/** A display rendition (a JPEG beside an original the browser cannot paint): the SDK's old retry window. */
const RENDITION_DEADLINE_MS = 10 * 60_000;

/** A fresh-enough ID token, or a rejection after ID_TOKEN_DEADLINE_MS. */
async function idTokenWithin(): Promise<string | undefined> {
  const user = getAuth().currentUser;
  if (!user) return undefined;
  return withDeadline(user.getIdToken(), ID_TOKEN_DEADLINE_MS, 'getIdToken');
}

/**
 * Stop the upload carrying `file`, because its person removed it (2026-10-08:
 * removing a chip used to leave its upload running to the end — a 50 GB file
 * the person no longer wanted would hold the line for an hour). Returns whether
 * a running transfer was stopped. Never throws; a file with no upload in flight
 * is simply marked so no later retry starts one.
 */
export function cancelAttachmentUpload(file: File | null | undefined): boolean {
  if (!file) return false;
  markUploadCancelled(file);
  inFlightByFile.delete(file);
  return cancelRegisteredUpload(file);
}

/**
 * Upload a file directly to GCS via a pre-signed URL.
 *
 * Phase A: Cloud Parallel Ingestion — browser uploads directly to GCS,
 * bypassing the Cloud Function proxy body entirely.
 *
 * ONE request, no resume: since 2026-10-08 it carries only files up to
 * SINGLE_PUT_MAX_BYTES, as the rescue for a file the resumable lane could not
 * place. A fresh upload is read by the cloud lane through a server-side COPY
 * of its stored object (cloudCopies.ts, planned by cloudCopyPlan.ts), never a second upload.
 *
 * Flow:
 *   1. POST to prest0nGenerateUploadUrls Cloud Function with file name
 *   2. Receive signed URL + gcsPath
 *   3. PUT file directly to GCS using signed URL
 *   4. Return gcsPath for inclusion in turn request
 *
 * @param file - The File object to upload
 * @param sessionKey - Current session key
 * @param accountPath - Account path for GCS path scoping
 * @returns Upload result with gcsPath (if successful)
 */
export async function uploadAttachmentViaGcsSignedUrl(
  file: File,
  sessionKey: string,
  accountPath: string,
): Promise<StorageUploadResult & { gcsPath?: string }> {
  if (file.size > SINGLE_PUT_MAX_BYTES) {
    return { success: false, error: new Error(`${file.size} bytes is past the single-PUT lane's ${SINGLE_PUT_MAX_BYTES}`) };
  }
  try {
    const token = await idTokenWithin();
    if (!token) {
      return { success: false, error: new Error('No auth token for GCS upload') };
    }

    // Step 1: Get signed URL from Cloud Function
    const urlResponse = await fetch(
      `${CF_BASE_URL}/prest0nGenerateUploadUrls`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileNames: [file.name],
          sessionKey,
          accountPath,
          mimeTypes: [file.type || 'application/octet-stream'],
        }),
        signal: AbortSignal.timeout(15_000),
      },
    );

    if (!urlResponse.ok) {
      const errText = await urlResponse.text().catch(() => 'unknown');
      throw new Error(`Signed URL request failed (${urlResponse.status}): ${errText}`);
    }

    const { urls } = (await urlResponse.json()) as {
      urls: Array<{ fileName: string; signedUrl: string; gcsPath: string; expiresAt: number }>;
    };

    if (!urls || urls.length === 0) {
      throw new Error('No signed URL returned');
    }

    const { signedUrl, gcsPath } = urls[0];

    // Step 2: PUT the file directly to GCS.
    //
    // The File is handed to fetch AS IS (2026-09-10). It used to be read
    // into an ArrayBuffer first, which put a second full copy of every
    // attachment in memory for the whole transfer — 398MB of it on the
    // 50-clip packet, on top of the base64 copies removed in #1820/#1822.
    // A File is a Blob; fetch streams it from disk and sets Content-Length
    // itself, so the copy bought nothing.
    const putResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
      signal: AbortSignal.timeout(putTimeoutMsForBytes(file.size)),
    });

    if (!putResponse.ok) {
      const errText = await putResponse.text().catch(() => 'unknown');
      throw new Error(`GCS PUT failed (${putResponse.status}): ${errText}`);
    }

    console.log(`[AttachmentStorage] GCS upload succeeded: ${gcsPath}`);
    return {
      success: true,
      gcsPath,
      storagePath: gcsPath, // reuse field for display
    };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.warn('[AttachmentStorage] GCS signed URL upload failed:', err.message);
    return { success: false, error: err };
  }
}
/**
 * @param file - The File object to upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @returns Upload result with download URL and storage path
 */
async function uploadAttachmentViaProxy(
  file: File,
  uid: string,
  messageId: string
): Promise<StorageUploadResult> {
  // The token first, bounded on its own, so the upload's 60 s below is all
  // the upload's.
  let token: string | undefined;
  try {
    token = await idTokenWithin();
  } catch (error) {
    return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
  }
  if (!token) {
    return { success: false, error: new Error('No auth token for proxy upload') };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60_000);

  try {
    // The File goes to fetch as is — see the GCS PUT above. This proxy
    // lane had the same full-copy read; fixing only one of the two would
    // have left every attachment duplicated in memory on whichever lane the
    // account happens to use.
    //
    // `size` (2026-10-08): the server refuses a body whose length differs
    // from it, before anything is stored, and answers with the stored length
    // — so a short body can never become a delivered file on this lane.
    const params = new URLSearchParams({
      filename: file.name,
      messageId,
      contentType: file.type || 'application/octet-stream',
      size: String(file.size),
    });

    const response = await fetch(
      `${CF_BASE_URL}/prest0nUploadFile?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/octet-stream',
        },
        body: file,
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      const errBody = await response.text().catch(() => 'unknown error');
      throw new Error(`Proxy upload failed (${response.status}): ${errBody}`);
    }

    const result = await response.json();
    // A server from before the size check answers without `size`; every
    // later one answers with the stored length, and a mismatch is a failure.
    const storedBytes = typeof result?.size === 'number' ? result.size : undefined;
    if (storedBytes !== undefined && storedBytes !== file.size) {
      throw new UploadSizeMismatchError(file.size, storedBytes);
    }
    console.log('[AttachmentStorage] Proxy upload succeeded');
    return {
      success: true,
      storageUrl: result.storageUrl,
      storagePath: result.storagePath,
      ...(storedBytes !== undefined ? { storedBytes } : {}),
    };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    console.warn('[AttachmentStorage] Proxy upload failed:', err.message);
    return { success: false, error: err };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Upload a single file to Firebase Storage with retry logic.
 * Tries the server-side CF proxy first (immune to ad-blockers) for a file the
 * proxy can carry, then the direct, resumable Firebase Storage upload.
 *
 * WHOLE OR NOT AT ALL (2026-10-08): a success means the stored object's length
 * was read back and equals the picked file's. An 11-minute recording that
 * arrives as one minute is the failure this exists to make impossible to
 * mistake for a delivery.
 *
 * @param file - The File object to upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @returns Upload result with download URL and storage path
 */
export async function uploadAttachmentToStorage(
  file: File,
  uid: string,
  messageId: string,
  maxRetries: number = 3
): Promise<StorageUploadResult> {
  // While this runs, the tab holds its own reloads (isAnyUploadMoving).
  const activity = beginUploadActivity();
  try {
    // ── Strategy 1: Server-side proxy upload (ad-blocker immune) ──
    //
    // ONLY FOR A FILE THE PROXY CAN CARRY (2026-09-14, tightened 2026-10-08).
    // The proxy hands the file to a Cloud Function as its raw request body:
    // the function must hold it in memory, and Cloud Run refuses a body over
    // 32 MiB before the function runs at all. Trying it with anything larger
    // would upload up to 32 MiB only to be refused; such a file goes straight
    // to the resumable lane, which serves the product limit.
    if (file.size <= PROXY_LANE_MAX_BYTES) {
      const proxyResult = await uploadAttachmentViaProxy(file, uid, messageId);
      if (proxyResult.success) {
        return proxyResult;
      }
      if (isUploadCancelled(file)) return { success: false, error: new UploadCancelledError() };
      console.warn('[AttachmentStorage] Proxy failed, falling back to direct SDK upload');
    } else {
      console.log(
        `[AttachmentStorage] ${file.size} bytes — past the proxy lane's request limit; going direct to storage`,
      );
    }

    // ── Strategy 2: Direct, resumable Firebase Storage SDK upload ──
    //
    // Its own chunk (largeUploadLane.ts), fetched the first time a file needs
    // it: the slots, the stall watch, the keep-alive and the whole-file check
    // all live there. A chunk that cannot be fetched is a failed upload like
    // any other — the record stays "not delivered" and the next ask retries.
    let lane: LargeUploadLaneModule;
    try {
      lane = await withDeadline(loadLargeUploadLane(), LANE_LOAD_DEADLINE_MS, 'large-upload lane');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
    }
    return await lane.uploadDirect(file, uid, messageId, maxRetries, activity);
  } finally {
    activity.end();
  }
}

/**
 * Upload multiple files to Firebase Storage in parallel.
 *
 * @param files - Array of File objects to upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @returns Array of upload results (same order as input files)
 */
export async function uploadAttachmentsToStorage(
  files: File[],
  uid: string,
  messageId: string
): Promise<StorageUploadResult[]> {
  return Promise.all(
    files.map((file) => uploadAttachmentToStorage(file, uid, messageId))
  );
}

/**
 * Serialize a File object with Firebase Storage upload.
 * Creates a SerializedAttachment with storage URL for cross-device persistence.
 *
 * @param file - The File object to serialize and upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @returns Serialized attachment with storage URL (if upload succeeded)
 */
export async function serializeAttachmentWithStorage(
  file: File,
  uid: string,
  messageId: string
): Promise<AttachmentWithStorage> {
  // Already going up since the user picked it? Then this IS that upload —
  // every caller (the send path, the summon-gate post lane) converges on the
  // one promise rather than starting a second transfer of the same bytes.
  const prewarmed = inFlightByFile.get(file);
  if (prewarmed) return prewarmed;
  return serializeAttachmentNow(file, uid, messageId);
}

/**
 * Upload the display rendition beside its original (2026-09-12).
 *
 * Deliberately the SAME path family — `users/{uid}/attachments/{messageId}/…`
 * with a `.display.jpg` suffix on the filename — rather than a new one. The
 * one-door storage rule (#2008) mints every path from one place and the
 * deletion and export inventories are derived from that list, so a rendition
 * inside an existing family is covered by both for free; a new family would
 * have to be declared in both or the photo's display copy would quietly
 * survive its owner asking to be deleted.
 *
 * Returns nothing on any failure. A missing rendition is the honest file card,
 * which is exactly what a recipient had before this existed — it must never
 * be able to fail the send it rides along with.
 *
 * Bounded by RENDITION_DEADLINE_MS (2026-10-08): the send awaits it, and the
 * storage SDK's own retry window — which used to bound it at ten minutes — is
 * two hours since the 50 GB raise (firebaseStorage.ts), for the large files.
 */
async function uploadDisplayRendition(
  rendition: Blob,
  originalName: string,
  uid: string,
  messageId: string,
): Promise<{ displayUrl: string; displayPath: string } | undefined> {
  try {
    const displayPath = getAttachmentStoragePath(uid, messageId, `${originalName}.display.jpg`);
    const storageRef = ref(storage, displayPath);
    const upload = uploadBytes(storageRef, rendition, {
      contentType: 'image/jpeg',
      customMetadata: {
        renditionOf: originalName,
        uploadedAt: new Date().toISOString(),
      },
    }).then(() => getDownloadURL(storageRef));
    const displayUrl = await withDeadline(upload, RENDITION_DEADLINE_MS, 'display rendition');
    return displayUrl ? { displayUrl, displayPath } : undefined;
  } catch (err) {
    console.warn('[AttachmentStorage] display rendition upload failed:', err);
    return undefined;
  }
}

async function serializeAttachmentNow(
  file: File,
  uid: string,
  messageId: string
): Promise<AttachmentWithStorage> {
  // Generate base64 dataUrl as fallback for localStorage persistence
  // This ensures attachments remain downloadable even if storageUrl becomes unavailable
  // Firestore will strip dataUrl (too large), but localStorage preserves it via Fix 3 merge logic
  //
  // …but only for a file small enough for that fallback to exist (2026-09-09).
  // localStorage stops around 5 MB, so base64 of a multi-MB file is not a
  // fallback anywhere — it is read, held, and dropped. This is the SECOND
  // read of the same bytes on the cloud-upload path (`serializeAttachments`
  // in useGlobalChat did the first), which is how the 398 MB / 50-clip run
  // reached ~1 GB of live strings and killed the tab. Same ceiling, same
  // reason: above it an inline copy can never be used.
  const dataUrl = isInlineEligible(file.size)
    ? await new Promise<string | undefined>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(file);
    })
    : undefined;

  // Measured beside the upload, not before it: a decode must never delay the
  // first byte going out. Resolves to {} on anything it cannot read, and {}
  // means the record is exactly what it was before this existed.
  const imageMetaPromise = readAttachmentImageMeta(file).catch((): AttachmentImageArtifacts => ({ meta: {} }));

  try {
    // Upload to Firebase Storage
    const uploadResult = await uploadAttachmentToStorage(file, uid, messageId);
    const { meta: imageMeta, displayRendition } = await imageMetaPromise;

    // A format the recipient's browser may not paint gets a JPEG beside the
    // byte-identical original. Awaited AFTER the original is safely up, so a
    // slow or failing rendition can never delay or break the actual file.
    const rendition = displayRendition
      ? await uploadDisplayRendition(displayRendition, file.name, uid, messageId)
      : undefined;

    // Return serialized attachment with storage info AND dataUrl fallback
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
      // Primary: Firebase Storage URL for cross-device persistence
      storageUrl: uploadResult.storageUrl,
      storagePath: uploadResult.storagePath,
      // When the signed URL was minted — the image tile re-mints past six days
      // so a picture never breaks on day 8 (chat-thread PR-6, 2026-09-09).
      storageUrlMintedAt: Date.now(),
      // Dimensions + a tiny placeholder so every reader paints the box, and
      // something in it, the instant the message lands (2026-09-11).
      ...imageMeta,
      // A displayable copy for browsers that cannot decode the original
      // (2026-09-12, HEIC). Absent ⇒ today's behaviour byte for byte.
      ...(rendition || {}),
      // Fallback: base64 dataUrl for localStorage persistence
      // Stripped by Firestore (1MB limit) but preserved in localStorage
      dataUrl,
    };
  } catch (error) {
    // If upload fails completely (e.g., CORS error), return with dataUrl fallback
    console.warn('[AttachmentStorage] serializeAttachmentWithStorage failed:', error);
    return {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
      // No storageUrl - dataUrl serves as the only download source
      dataUrl,
    };
  }
}

/**
 * Serialize multiple File objects with Firebase Storage upload.
 *
 * @param files - Array of File objects to serialize and upload
 * @param uid - User ID (Firebase Auth UID)
 * @param messageId - Unique message identifier
 * @returns Array of serialized attachments with storage URLs
 */
/**
 * How many uploads may be in flight at once.
 *
 * Browsers open roughly six connections per host, so firing fifty `fetch`
 * calls does NOT give fifty transfers — it gives six transfers and
 * forty-four queued requests. That matters because each one carries
 * `AbortSignal.timeout(300_000)`, and the clock starts when fetch is called,
 * not when the request gets a connection. A file far back in the queue can
 * therefore spend its entire five-minute budget waiting for a slot and abort
 * without sending a byte — the more files, the likelier it is, which is the
 * opposite of how a batch should behave.
 *
 * Starting only as many as can actually run means each timeout measures a
 * transfer, which is what it was written to measure.
 */
export const UPLOAD_CONCURRENCY = 6;

/** Map with a bounded number of workers, preserving input order. */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      out[i] = await fn(items[i], i);
    }
  });
  await Promise.all(workers);
  return out;
}

export async function serializeAttachmentsWithStorage(
  files: File[],
  uid: string,
  messageId: string
): Promise<AttachmentWithStorage[]> {
  return mapWithConcurrency(files, UPLOAD_CONCURRENCY, (file) =>
    serializeAttachmentWithStorage(file, uid, messageId)
  );
}

/**
 * Check if the current user is authenticated for Storage uploads.
 * Returns the user's UID if authenticated, undefined otherwise.
 *
 * FH-QA-T2.1 (2026-05-21): rewritten to use the already-imported
 * `getAuth` from `firebase/auth` rather than a dynamic CommonJS
 * `require('firebase/auth')`. In some Webpack/Next/Vite production
 * bundle configurations the `require()` call cannot resolve and
 * throws synchronously — which, when invoked from inside
 * `handleSend()` in useGlobalChat, took down the entire post-bubble
 * send pipeline (attachment upload AND the orchestrator turn POST
 * to prest0nRunTurn) before any user-visible error could surface.
 * That produced the exact 12:46 UTC repro Malaika hit on Fernando's
 * webchat: file pill renders, user bubble renders, then complete
 * silence — no upload, no turn, no error.
 *
 * Bonus: hard-wrap in try/catch so any future auth-init edge case
 * degrades to "no upload" rather than "no reply at all".
 */
export function getCurrentUserUid(): string | undefined {
  try {
    const auth = getAuth();
    return auth.currentUser?.uid;
  } catch (err) {
    console.warn('[AttachmentStorage] getCurrentUserUid threw — returning undefined:', err);
    return undefined;
  }
}
